import { useState } from 'react';
import { Box, Divider, Typography, useMediaQuery } from '@mui/material';
import {
  ChipBadge,
  DocumentViewer,
  IStyles,
  IconWrapper,
  SelectMenu,
  convertPercentageToMarks,
  deserify,
  getDifficultyLevelString,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import { useNavigate } from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../reduxStore/reduxHooks';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { BloomLevelBreackdown } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { ChartBox } from '../../../ManageHomework/CompletedHomework/components/ChartBox';
import {
  AttemptStats,
  AttemptTypeEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { StrengthsWeaknessesSection } from '../../../ManageHomework/CompletedHomework/components/StrenghtsAndWeaknessSection';
import { HeaderWithProgressBar } from '../../../../components/HeaderWithProgressBar';
import { OverviewBox } from '../../../../components/OverviewBox';
import { SelectAttemptForReportPopup } from './SelectAttemptForReportPopup';
import { createIOptionListFromAttemptStats } from '../../manageHWutils';
import { setToastInfo } from '../../../Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import { ASSESSMENT_VIEW } from '../../../../routeHandling/RoutesNomenclature';
import {
  IStatusObj,
  setClickerAssessmentInfo,
} from '../../../Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { setCurrentAssessmentClassStats } from '../../reducer/manageHomework.slice';
import { BestAttemptCheck } from './BestAttemptCheck';
import { getSubjectNameFromEnum } from '../../../Home/home.utils';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(24), md: pxTovW(30) },
    width: { xs: '100%', md: '100%', lg: '100%' },
    boxSizing: 'border-box',
  },
};

export interface IOptionList {
  name: string | number;
  id: number;
  nonEditableItem?: boolean;
  selectedNumber?: number;
}
export const CAEndedHwLeftPanel = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teacher_id = getLocalStorage('userId');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpadOnly = useMediaQuery(theme.breakpoints.only('md'));
  const { setShowAppLoader } = useGlobalContext();
  const {
    SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const { selected_assessment_id, current_assessmemt_classStats } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const { clicker_assessment_info } = deserify(
    useAppSelector((state) => state.clickerAssessment)
  );
  const [bestAttemptChecked, setBestAttemptChecked] = useState(false);
  const [viewReportPopup, setViewReportPopup] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);

  // ----------------------------------------------------------------

  const createChartData = (bloomData: BloomLevelBreackdown) => {
    const mappings = [
      { key: 'retentionScore', title: 'Retention', color: '#005DAA' },
      { key: 'comprehensionScore', title: 'Comprehension', color: '#3498DB' },
      { key: 'applicationScore', title: 'Application', color: '#5DC151' },
      { key: 'analysisScore', title: 'Analysis', color: '#FA8D49' },
      { key: 'creationScore', title: 'Creation', color: '#00BFC8' },
      { key: 'evaluationScore', title: 'Evaluation', color: '#E80000' },
    ] as const;
    const hasValidData = mappings.some(
      ({ key }) =>
        bloomData[key] !== undefined &&
        bloomData[key] !== null &&
        bloomData[key] !== 0
    );

    if (!hasValidData) {
      return []; // Return an empty array if no valid data is present
    }

    return mappings.map(({ key, title, color }) => ({
      obtainedMarks: bloomData[key] || 0,
      maxMarks: 100,
      title,
      color,
    }));
  };

  // ----------------------------------------------------------------

  const fetchSchoolAssessmentReport = async () => {
    try {
      setShowAppLoader(true);
      const selectedAttempt = clicker_assessment_info?.selected_attempt;
      const res =
        await SchoolAssessmentAPIServiceV3ClientWithStatusCodeHandler.fetchSchoolAssessmentReport(
          {
            assessmentId: selected_assessment_id,
            teacherId: BigInt(teacher_id),
            attemptNumber: bestAttemptChecked
              ? undefined
              : selectedAttempt?.attemptNumber,
            attemptType: bestAttemptChecked
              ? AttemptTypeEnum.ATTEMPT_TYPE_BEST
              : undefined,
          }
        );
      if (res.data?.gcpReportUrl) {
        setPdfUrl(getMediaBasePath(res.data.gcpReportUrl));
      } else {
        dispatch(
          setToastInfo({
            label: res.message,
            variant: 'success',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    } catch (err) {
      if (err instanceof ConnectError) {
        console.log('Error:', err.rawMessage);
        dispatch(
          setToastInfo({
            label: err.rawMessage,
            variant: 'error',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while generating report',
            variant: 'error',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    }
  };

  // ----------------------------------------------------------------

  const getAssessmentClassStats = async ({
    changedAttemptInfo,
    bestAttempt,
  }: {
    changedAttemptInfo?: AttemptStats;
    bestAttempt?: boolean;
  }) => {
    try {
      setShowAppLoader(true);
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getAssessmentClassStats(
          {
            teacherId: BigInt(teacher_id),
            assessmentId: selected_assessment_id,
            attemptNumber: bestAttempt
              ? undefined
              : changedAttemptInfo?.attemptNumber,
            attemptType: bestAttempt
              ? AttemptTypeEnum.ATTEMPT_TYPE_BEST
              : undefined,
          }
        );
      if (response.data) {
        // console.log(response.data);
        const StatusObj: IStatusObj = {};
        response.data.studentPerformanceInfo.forEach((e) => {
          const id = e.studentId.toString();
          StatusObj[id] = {
            studentId: e.studentId,
            isSubmitted: e.isSubmitted || false,
            marks: convertPercentageToMarks(
              e.scorePercent,
              response.data?.assessment?.maxMarks
            ),
          };
        });
        dispatch(setCurrentAssessmentClassStats(response.data));
        dispatch(
          setClickerAssessmentInfo({
            ...clicker_assessment_info,
            statusInfo: StatusObj,
            selected_attempt: bestAttempt ? undefined : changedAttemptInfo,
          })
        );
        setShowAppLoader(false);
      } else {
        // dispatch(setClickerAssessmentInfo(undefined));
        // dispatch(setCurrentAssessmentClassStats(undefined));
        dispatch(
          setToastInfo({
            label: `Data for Attempt ${changedAttemptInfo?.attemptNumber} could not be fetched.`,
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (err) {
      setShowAppLoader(false);
      console.log(err);
      dispatch(
        setToastInfo({
          label: `Data for Attempt ${changedAttemptInfo?.attemptNumber} could not be fetched.`,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // ----------------------------------------------------------------

  const onChangeCheckbox = () => {
    setBestAttemptChecked((prevState) => {
      const attempts: AttemptStats[] | undefined =
        current_assessmemt_classStats?.attemptInfo;
      const latestAttempt =
        attempts?.find(
          (data) => data.attemptType === AttemptTypeEnum.ATTEMPT_TYPE_LATEST
        ) ?? attempts?.[attempts?.length - 1];
      if (prevState) {
        // changing to false state i.e. latest attempt
        getAssessmentClassStats({
          changedAttemptInfo: latestAttempt,
          bestAttempt: false,
        });
      } else {
        // changing to true state i.e. best attempt
        getAssessmentClassStats({
          changedAttemptInfo: latestAttempt,
          bestAttempt: true,
        });
      }
      return !prevState;
    });
  };
  const classSection = [
    current_assessmemt_classStats?.assessment?.class,
    current_assessmemt_classStats?.assessment?.section,
  ]
    .filter(Boolean)
    .join(' ');
  const subtitleParts = [
    classSection,
    current_assessmemt_classStats?.assessment?.subject,
    current_assessmemt_classStats?.assessment?.moduleName,
  ]
    .filter(Boolean)
    .join(' | ');

  // ----------------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <HeaderWithProgressBar
        imagePath={getMediaBasePath(
          current_assessmemt_classStats?.assessment?.assessmentPosterImgUrl,
          'processedMediaBucket'
        )}
        primaryTitle={
          current_assessmemt_classStats?.assessment?.assessmentTitle || 'title'
        }
        secondaryTitle={subtitleParts}
        progressBarScore={
          Math.round(
            Number(
              current_assessmemt_classStats?.assessment?.classScore?.toFixed(2)
            )
          ) || 0
        }
      />

      {isMobile && (
        <OverviewBox
          assessmentTargetDate={
            current_assessmemt_classStats?.assessment?.assessmentTargetDate
          }
          difficultyLevel={
            current_assessmemt_classStats?.assessment?.difficultyLevel
          }
          numberOfQuestions={
            current_assessmemt_classStats?.assessment?.assessmentContentInfo
              ?.numberOfQuestions
          }
          viewAssessmentCTA={() => navigate(`${ASSESSMENT_VIEW}/ended`)}
        />
      )}

      <SelectMenu
        placeholder="select"
        value={
          current_assessmemt_classStats?.attemptInfo?.length === 0
            ? 'No Attempts'
            : bestAttemptChecked
            ? 'Best Attempt'
            : `Attempt ${
                clicker_assessment_info?.selected_attempt?.attemptNumber || ''
              }`
        }
        disabled={
          current_assessmemt_classStats?.attemptInfo?.length === 0 ||
          bestAttemptChecked
        }
        onChange={(value: string) => {
          getAssessmentClassStats({
            changedAttemptInfo:
              current_assessmemt_classStats?.attemptInfo?.find(
                (item) => item.attemptNumber === Number(value.split(' ')[1])
              ),
          });
        }}
        optionList={createIOptionListFromAttemptStats(
          current_assessmemt_classStats?.attemptInfo
        )}
        rootStyles={{
          width: { xs: '100%', md: '100%', lg: '100%' },
          height: { xs: pxToRem(55), md: pxTovW(75), lg: pxTovW(65) },
          '& .Mui-disabled': { backgroundColor: '#F5FAFF' },
        }}
        selectedBold
        menuItemStyles={{
          padding: { xs: pxToRem(10), md: pxTovW(20), lg: pxTovW(20) },
        }}
      />

      <BestAttemptCheck
        checkedState={bestAttemptChecked}
        onChangeCheckbox={onChangeCheckbox}
        labelText=" Choose the the Best Attempt"
        disabled={current_assessmemt_classStats?.attemptInfo?.length === 0}
      />

      {current_assessmemt_classStats?.assessmentReportInfo
        ?.bloomLevelBreackdown &&
        current_assessmemt_classStats?.assessment?.taskType !==
          TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM && (
          <ChartBox
            assessmentType={
              current_assessmemt_classStats?.assessment?.assessmentType
            }
            homeworkId={selected_assessment_id}
            chartData={createChartData(
              current_assessmemt_classStats?.assessmentReportInfo
                ?.bloomLevelBreackdown
            )}
            reportButtonHandler={() => {
              // setViewReportPopup(true);
              fetchSchoolAssessmentReport();
            }}
            reportSubject={current_assessmemt_classStats?.assessment?.subjects.join(
              ', '
            )}
            reportClassName={current_assessmemt_classStats?.assessment?.class}
            reportSection={current_assessmemt_classStats?.assessment?.section}
          />
        )}
      {current_assessmemt_classStats?.assessment?.taskType !==
        TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM &&
      current_assessmemt_classStats?.assessment?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM &&
      current_assessmemt_classStats?.multiSubjectMetrics &&
      current_assessmemt_classStats?.multiSubjectMetrics?.length > 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(16), md: pxTovW(24) },
            width: '100%',
            padding: { xs: pxToRem(12), md: pxTovW(14) },
            boxSizing: 'border-box',
            marginBottom: { xs: pxToRem(12), md: pxTovW(12) },
          }}
        >
          <Typography variant="h3" fontWeight="bold" sx={{ color: '#222222' }}>
            Subject Level Summary
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: pxToRem(8), md: pxTovW(8) },
            }}
          >
            {current_assessmemt_classStats?.multiSubjectMetrics?.map(
              (section) => (
                <Box
                  key={section.subject}
                  sx={{
                    width: '100%',
                    border: '1px solid #ECECEC',
                    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
                    backgroundColor: '#FFFFFF',
                    padding: { xs: pxToRem(10), md: pxTovW(10) },
                    boxSizing: 'border-box',
                    gap: { xs: pxToRem(10), md: pxTovW(10) },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    boxShadow: `0px 0px ${pxToRem(10)} #0000001F`,
                  }}
                >
                  <ChipBadge
                    label={`${getSubjectNameFromEnum(section?.subject)} ${
                      section?.sectionTitle
                    }`}
                    color="primary"
                    size="small"
                    sx={{
                      fontSize: pxToRem(10),
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                      gap: { xs: pxToRem(8), md: pxTovW(8) },
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: pxToRem(5), md: pxTovW(10) },
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                      >
                        <IconWrapper
                          name="questions"
                          size={isIpadOnly ? 'large' : 'md'}
                          parentFolder="icons"
                          type="png"
                        />
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          sx={{ color: '#1F1F1F' }}
                        >
                          {section.numberOfQuestions || 0}
                        </Typography>
                      </Box>
                      <Typography variant="smallText" sx={{ color: '#777777' }}>
                        Questions
                      </Typography>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ backgroundColor: '#E8E8E8' }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: pxToRem(5), md: pxTovW(10) },
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                      >
                        <IconWrapper
                          name="bar-graph"
                          size={isIpadOnly ? 'large' : 'md'}
                          parentFolder="icons"
                          type="png"
                        />
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          sx={{ color: '#1F1F1F' }}
                        >
                          {getDifficultyLevelString(section.avgDifficultyLevel)}
                        </Typography>
                      </Box>
                      <Typography variant="smallText" sx={{ color: '#777777' }}>
                        Difficulty
                      </Typography>
                    </Box>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ backgroundColor: '#E8E8E8' }}
                    />

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: pxToRem(5), md: pxTovW(10) },
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: pxToRem(5), md: pxTovW(10) },
                        }}
                      >
                        <IconWrapper
                          name="percentage-primary"
                          size={isIpadOnly ? 'large' : 'md'}
                          parentFolder="icons"
                          type="png"
                        />
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          sx={{ color: '#1F1F1F' }}
                        >
                          {section.classSubjectMaxScore || 0}
                        </Typography>
                      </Box>
                      <Typography variant="smallText" sx={{ color: '#777777' }}>
                        Total Marks
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )
            )}
          </Box>
        </Box>
      ) : (
        <></>
      )}
      {current_assessmemt_classStats?.assessment?.taskType !==
        TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM && (
        <StrengthsWeaknessesSection
          resultData={{
            strengths:
              current_assessmemt_classStats?.assessmentReportInfo
                ?.strengthsAndWeaknessess?.strength || [],
            weakness:
              current_assessmemt_classStats?.assessmentReportInfo
                ?.strengthsAndWeaknessess?.weakness || [],
          }}
        />
      )}

      {/* Not in use */}
      <SelectAttemptForReportPopup
        open={viewReportPopup}
        handleClose={() => {
          setViewReportPopup((prevState) => !prevState);
        }}
        closeIcon
        selectedAttempt={
          current_assessmemt_classStats?.attemptInfo?.length === 0
            ? 'No Attempts'
            : bestAttemptChecked
            ? 'Best Attempt'
            : `Attempt ${clicker_assessment_info?.selected_attempt?.attemptNumber}`
        }
        attemptList={createIOptionListFromAttemptStats(
          current_assessmemt_classStats?.attemptInfo
        )}
        onChangeSelectMenu={(value: string) => {
          dispatch(
            setClickerAssessmentInfo({
              ...clicker_assessment_info,
              selected_attempt:
                current_assessmemt_classStats?.attemptInfo?.find(
                  (item) => item.attemptNumber === Number(value.split(' ')[1])
                ),
            })
          );
        }}
        bestAttemptChecked={bestAttemptChecked}
        onChangeCheckbox={onChangeCheckbox}
        confirmSelectionCTA={() => {
          // console.log('confirmSelectionCTA:');
          fetchSchoolAssessmentReport();
        }}
      />

      {pdfUrl && (
        <DocumentViewer
          downloadUrl={pdfUrl}
          previewUrl={pdfUrl}
          gcpUrl={pdfUrl}
          type={FileEnum.FILE_TYPE_PDF}
          fileName={
            current_assessmemt_classStats?.assessment?.subjects ||
            current_assessmemt_classStats?.assessment?.class ||
            current_assessmemt_classStats?.assessment?.section
              ? `${
                  current_assessmemt_classStats?.assessment?.subjects.join(
                    ', '
                  ) || ''
                }_${current_assessmemt_classStats?.assessment?.class || ''}_${
                  current_assessmemt_classStats?.assessment?.section || ''
                }_Geneo_Assessment_Report_Teacher_${selected_assessment_id}.pdf`
              : `_Geneo_Assessment_Report_Teacher_${selected_assessment_id}.pdf`
          }
          open={!!pdfUrl}
          closeFile={() => {
            setPdfUrl(undefined);
          }}
        />
      )}
    </Box>
  );
};
